import { api, getSessionToken, TENANT_CODE } from './api';

export type OptimizedImage={name:'thumb'|'card'|'zoom'|'source';blob:Blob;mimeType:string;bytes:number;width:number;height:number};

type DecodedImage={source:CanvasImageSource;width:number;height:number;close:()=>void};

async function decodeImage(file:File):Promise<DecodedImage>{
  if(typeof createImageBitmap==='function')try{
    const bitmap=await createImageBitmap(file,{imageOrientation:'from-image'});
    return{source:bitmap,width:bitmap.width,height:bitmap.height,close:()=>bitmap.close()};
  }catch{/* iOS and HEIC can require the native image decoder fallback. */}
  const url=URL.createObjectURL(file);
  try{
    const image=new Image();image.decoding='async';
    await new Promise<void>((resolve,reject)=>{image.onload=()=>resolve();image.onerror=()=>reject(new Error('This photo format could not be opened on this device. Try JPEG, PNG, WebP, HEIC, or HEIF.'));image.src=url;});
    return{source:image,width:image.naturalWidth,height:image.naturalHeight,close:()=>URL.revokeObjectURL(url)};
  }catch(error){URL.revokeObjectURL(url);throw error;}
}

async function canvasBlob(canvas:HTMLCanvasElement,quality:number){
  const encode=(type:string)=>new Promise<Blob|null>(resolve=>canvas.toBlob(resolve,type,quality));
  const webp=await encode('image/webp');
  if(webp?.type==='image/webp')return{blob:webp,mimeType:'image/webp'};
  const jpeg=await encode('image/jpeg');
  if(jpeg)return{blob:jpeg,mimeType:'image/jpeg'};
  throw new Error('Could not optimize this photo on your device.');
}

async function renderVariant(image:DecodedImage,name:OptimizedImage['name'],maxEdge:number,quality:number):Promise<OptimizedImage>{
  const scale=Math.min(1,maxEdge/Math.max(image.width,image.height));const width=Math.max(1,Math.round(image.width*scale));const height=Math.max(1,Math.round(image.height*scale));
  const canvas=document.createElement('canvas');canvas.width=width;canvas.height=height;const context=canvas.getContext('2d',{alpha:false});if(!context)throw new Error('Image processing is unavailable on this device.');context.fillStyle='#fff';context.fillRect(0,0,width,height);context.drawImage(image.source,0,0,width,height);
  const encoded=await canvasBlob(canvas,quality);
  canvas.width=1;canvas.height=1;
  return{name,blob:encoded.blob,mimeType:encoded.mimeType,bytes:encoded.blob.size,width,height};
}

export async function optimizeImage(file:File){
  const imageExtension=/\.(?:jpe?g|png|webp|avif|heic|heif)$/i.test(file.name);if(!file.type.startsWith('image/')&&!imageExtension)throw new Error('Choose an image file');if(file.size>25*1024*1024)throw new Error('Image must be smaller than 25 MB');
  const image=await decodeImage(file);try{
    const swatch=document.createElement('canvas');swatch.width=1;swatch.height=1;const context=swatch.getContext('2d')!;context.drawImage(image.source,0,0,1,1);const [r,g,b]=context.getImageData(0,0,1,1).data;const dominantColor=`#${[r,g,b].map(value=>value.toString(16).padStart(2,'0')).join('')}`;
    const variants:OptimizedImage[]=[];
    for(const [name,maxEdge,quality] of [['thumb',320,.76],['card',960,.82],['zoom',1600,.84],['source',2400,.88]] as const)variants.push(await renderVariant(image,name,maxEdge,quality));
    return{variants,dominantColor};
  }finally{image.close();}
}

export async function uploadOptimizedImage(file:File,purpose:'product'|'wardrobe'|'body_reference'|'scene_reference'){
  const optimized=await optimizeImage(file);const prepared=await api<{assetId:string;canonicalUrl:string;uploads:Array<{name:OptimizedImage['name'];upload:{url:string;method:string;headers:Record<string,string>}}>}>('/api/uploads/prepare',{method:'POST',body:JSON.stringify({fileName:file.name,purpose,dominantColor:optimized.dominantColor,variants:optimized.variants.map(({name,mimeType,bytes,width,height})=>({name,mimeType,bytes,width,height}))})},true);
  const blobs=new Map(optimized.variants.map(item=>[item.name,item.blob]));
  for(const item of prepared.uploads){
    const sameOrigin=item.upload.url.startsWith('/');const token=getSessionToken();
    const headers:Record<string,string>={...item.upload.headers};
    if(sameOrigin){if(token)headers.Authorization=`Bearer ${token}`;headers['x-tenant-code']=TENANT_CODE;}
    const response=await fetch(item.upload.url,{method:'PUT',headers,body:blobs.get(item.name)});if(!response.ok)throw new Error(`Upload failed for ${item.name}`);
  }
  return api<{id:string;canonicalUrl:string;displayUrl:string;variants:Record<string,{url:string;width:number;height:number;mimeType:string}>;dominantColor?:string}>('/api/uploads/confirm',{method:'POST',body:JSON.stringify({assetId:prepared.assetId})},true);
}
